import { useMemo } from "react";

import { useCabinetsIn, useCollection } from "@/lib/ddb-hooks";
import type { EdgeBand, Hardware, Id, Material } from "@/lib/types";
import { indexCatalogs } from "./bom";
import { nestProject, type ProjectNesting } from "./nest";

export interface UseNestingState {
  nesting: ProjectNesting | null;
  isLoading: boolean;
  error: Error | null;
}

/** Memoised nesting computation; re-runs only when cabinets or catalog rows change. */
export function useNesting(projectId: Id | null | undefined): UseNestingState {
  const m = useCollection<Material>("materials");
  const e = useCollection<EdgeBand>("edge_bands");
  const h = useCollection<Hardware>("hardware");
  const c = useCabinetsIn(projectId);

  const nesting = useMemo(() => {
    if (!m.data || !e.data || !h.data || !c.data) return null;
    const cat = indexCatalogs(m.data, e.data, h.data);
    return nestProject(c.data, cat);
  }, [m.data, e.data, h.data, c.data]);

  const isLoading = m.isLoading || e.isLoading || h.isLoading || c.isLoading;
  const error =
    (m.error as Error | null) ??
    (e.error as Error | null) ??
    (h.error as Error | null) ??
    (c.error as Error | null) ??
    null;

  return { nesting, isLoading, error };
}
